import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  ChevronLeft, ChevronRight, Check, CarFront, MessageCircle, ArrowRight,
} from 'lucide-react';
import { db, submitCarHireRequest } from '@/lib/firebase';
import { useBusiness } from '@/lib/business';
import { waLink } from '@/data/site';
import { HIRE_POLICY } from '@/data/fleet';
import { useFleet } from '@/lib/useFleet';
import { Modal } from '@/components/ui/Modal';
import { formatPula } from '@/lib/money';
import type { BlockedRange } from '@/lib/types';

/* ── date helpers (local, YYYY-MM-DD) ── */
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromYmd = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const niceDate = (s: string) => (s ? fromYmd(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Blocked dates for a single vehicle id (includes ranges with no carId = all vehicles). */
function blockedSet(ranges: BlockedRange[], carId: string): Set<string> {
  const set = new Set<string>();
  for (const r of ranges) {
    if (r.carId && carId && r.carId !== carId) continue;
    if (!r.carId && !r.carName && (r.roomId || r.roomName)) continue; // ignore room-only blocks
    let d = fromYmd(r.from); const end = fromYmd(r.to);
    let guard = 0;
    while (d <= end && guard < 800) { set.add(ymd(d)); d = addDays(d, 1); guard++; }
  }
  return set;
}
function monthCells(monthDate: Date): (Date | null)[] {
  const first = startOfMonth(monthDate);
  const startWeekday = (first.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = Array(startWeekday).fill(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(first.getFullYear(), first.getMonth(), day));
  return cells;
}

export function CarBooking({ initialCarId }: { initialCarId?: string } = {}) {
  const biz = useBusiness();
  const { active: fleet } = useFleet();
  const todayStr = ymd(new Date());

  const [ranges, setRanges] = useState<BlockedRange[]>([]);
  const [carId, setCarId] = useState('');
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [driverAge, setDriverAge] = useState('');
  const [notes, setNotes] = useState('');
  const [hp, setHp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    return onSnapshot(
      doc(db, 'availability', 'carCalendar'),
      (snap) => setRanges(snap.exists() ? ((snap.data().ranges as BlockedRange[]) || []) : []),
      () => setRanges([]),
    );
  }, []);

  // Choose the initial / default vehicle once the fleet has loaded.
  useEffect(() => {
    if (carId && fleet.some((v) => v.id === carId)) return;
    const wanted = initialCarId && fleet.some((v) => v.id === initialCarId) ? initialCarId : (fleet[0]?.id ?? '');
    if (wanted) setCarId(wanted);
  }, [fleet, initialCarId]); // eslint-disable-line

  const car = fleet.find((v) => v.id === carId);
  const blocked = useMemo(() => blockedSet(ranges, carId), [ranges, carId]);
  const selectingEnd = Boolean(pickup && !dropoff);
  const firstBlockedAfter = useMemo(
    () => (pickup ? [...blocked].filter((s) => s > pickup).sort()[0] : undefined),
    [blocked, pickup],
  );

  // reset selection if it now overlaps a block (e.g. after switching vehicles)
  useEffect(() => {
    if (pickup && dropoff) {
      let d = fromYmd(pickup); const end = fromYmd(dropoff);
      while (d < end) { if (blocked.has(ymd(d))) { setPickup(''); setDropoff(''); break; } d = addDays(d, 1); }
    }
  }, [carId]); // eslint-disable-line

  function disabled(d: Date): boolean {
    const s = ymd(d);
    if (s < todayStr) return true;
    if (blocked.has(s)) return true;
    if (selectingEnd) {
      if (s <= pickup) return true;
      if (firstBlockedAfter && s > firstBlockedAfter) return true;
    }
    return false;
  }
  function pick(d: Date) {
    const s = ymd(d);
    if (!pickup || (pickup && dropoff)) { setPickup(s); setDropoff(''); return; }
    if (s <= pickup) { setPickup(s); setDropoff(''); return; }
    setDropoff(s);
  }

  const days = pickup && dropoff ? Math.round((fromYmd(dropoff).getTime() - fromYmd(pickup).getTime()) / 86400000) : 0;
  const rate = car?.pricePerDay ?? 0;
  const total = rate * days;

  function flash(m: string) { setError(m); }
  function openConfirm() {
    setError('');
    if (!car) return flash('Please choose a vehicle.');
    if (!pickup || !dropoff || days < 1) return flash('Please select your pick-up and return dates.');
    if (!fullName.trim() || !email.trim()) return flash('Please add your name and email.');
    if (!phone.trim()) return flash('A phone number is required so we can confirm your booking.');
    setConfirming(true);
  }
  async function submit() {
    setError('');
    setConfirming(false);
    if (!car) return;
    setBusy(true);
    try {
      await submitCarHireRequest({
        hp, fullName: fullName.trim(), email: email.trim(), phone: phone.trim(),
        vehicleId: car.id, vehicleName: car.name,
        pickup, dropoff, days, pricePerDay: rate, totalPrice: total,
        driverAge: driverAge.trim(), notes: notes.trim(),
      });
      setDone(true);
    } catch (e) {
      flash(e instanceof Error ? `${e.message} You can also request on WhatsApp.` : 'Something went wrong.');
    } finally { setBusy(false); }
  }

  const canPrev = startOfMonth(month) > startOfMonth(new Date());
  const waText = `Hi Grace Haven, I'd like to hire the ${car?.name ?? 'car'} from ${niceDate(pickup)} to ${niceDate(dropoff)} (${days} day${days === 1 ? '' : 's'}).`;

  if (done) {
    return (
      <div className="mt-16 border border-gold/50 bg-paper p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/20 text-gold-deep"><CarFront size={30} /></div>
        <h2 className="mt-6 font-display text-3xl text-espresso">Hire requested</h2>
        <div className="rule-gold mx-auto mt-4" />
        <p className="mx-auto mt-5 max-w-md font-body text-muted">
          Thank you, {fullName.split(' ')[0] || 'and see you soon'}. We've emailed you a copy of your request for the {car?.name} ({niceDate(pickup)} – {niceDate(dropoff)}, {days} day{days === 1 ? '' : 's'}, {formatPula(total)}). We'll confirm availability shortly — no payment is taken now.
        </p>
      </div>
    );
  }

  function renderMonth(m: Date) {
    return (
      <div className="flex-1">
        <p className="mb-3 text-center font-display text-lg text-espresso">{MONTHS[m.getMonth()]} {m.getFullYear()}</p>
        <div className="grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => <div key={w} className="pb-1 text-center font-body text-[11px] font-semibold uppercase text-muted">{w}</div>)}
          {monthCells(m).map((d, i) => {
            if (!d) return <div key={i} />;
            const s = ymd(d);
            const dis = disabled(d);
            const isBlocked = blocked.has(s);
            const isStart = s === pickup, isEnd = s === dropoff;
            const inRange = pickup && dropoff && s > pickup && s < dropoff;
            const edge = isStart || isEnd;
            const stateClass = edge
              ? 'bg-gold text-espresso-deep font-semibold'
              : inRange
                ? 'bg-gold/20 text-espresso'
                : isBlocked
                  ? 'bg-blush-soft/60 text-blush-deep/80 line-through'
                  : dis
                    ? 'text-muted/40 line-through'
                    : 'text-espresso';
            return (
              <button key={i} type="button" disabled={dis} onClick={() => pick(d)}
                className={[
                  'relative aspect-square rounded-full text-sm transition',
                  stateClass,
                  dis ? 'cursor-not-allowed' : 'hover:bg-gold/15',
                ].join(' ')}>
                {d.getDate()}
                {isBlocked && (
                  <span className="pointer-events-none absolute right-1 top-1 text-[9px] leading-none text-blush-deep/70" aria-hidden>×</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-16">
      {/* Vehicle selector */}
      <div className="grid gap-3 sm:grid-cols-3">
        {fleet.map((v) => {
          const active = v.id === carId;
          return (
            <button key={v.id} type="button" onClick={() => setCarId(v.id)}
              className={`border p-4 text-left transition ${active ? 'border-gold bg-gold/10' : 'border-line bg-cream hover:border-gold'}`}>
              <p className="font-body text-[11px] uppercase tracking-wide text-muted">{v.category}</p>
              <p className="font-display text-lg text-espresso">{v.name}</p>
              <p className="mt-2 font-display text-xl text-espresso">{formatPula(v.pricePerDay)} <span className="font-body text-[11px] uppercase tracking-wide text-muted">/ day</span></p>
            </button>
          );
        })}
        {fleet.length === 0 && (
          <p className="col-span-full border border-line bg-paper p-4 font-body text-sm text-muted">Our fleet is being updated — please contact us to arrange a hire.</p>
        )}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Calendar */}
        <div className="border border-line bg-cream p-6">
          <div className="mb-4 flex items-center justify-between">
            <button type="button" disabled={!canPrev} onClick={() => setMonth(addMonths(month, -1))}
              className="rounded-full p-2 text-espresso transition hover:bg-gold/15 disabled:opacity-30"><ChevronLeft size={18} /></button>
            <p className="font-body text-xs uppercase tracking-wide text-muted">
              {selectingEnd ? 'Select return date' : 'Select pick-up date'}
            </p>
            <button type="button" onClick={() => setMonth(addMonths(month, 1))}
              className="rounded-full p-2 text-espresso transition hover:bg-gold/15"><ChevronRight size={18} /></button>
          </div>
          <div className="flex gap-8">
            {renderMonth(month)}
            <div className="hidden flex-1 md:block">{renderMonth(addMonths(month, 1))}</div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-line pt-3 font-body text-[11px] text-muted">
            <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-3.5 rounded-full bg-gold" /> Selected</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-3.5 rounded-full bg-gold/20" /> In your range</span>
            <span className="flex items-center gap-1.5">
              <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-blush-soft/60 text-[8px] leading-none text-blush-deep/80 line-through">×</span> Unavailable
            </span>
          </div>
        </div>

        {/* Reserve card */}
        <aside className="h-fit border border-line bg-paper p-6 lg:sticky lg:top-28">
          <p className="font-display text-2xl text-espresso">{formatPula(rate)} <span className="font-body text-sm text-muted">/ day</span></p>
          {car && <p className="mt-1 font-body text-sm text-muted">{car.name}</p>}
          <div className="rule-gold mt-3" />

          <div className="mt-5 grid grid-cols-2 border border-line">
            <div className="border-r border-line p-3">
              <p className="font-body text-[10px] uppercase tracking-wide text-muted">Pick-up</p>
              <p className="font-body text-sm text-espresso">{niceDate(pickup)}</p>
            </div>
            <div className="p-3">
              <p className="font-body text-[10px] uppercase tracking-wide text-muted">Return</p>
              <p className="font-body text-sm text-espresso">{niceDate(dropoff)}</p>
            </div>
          </div>

          <div className="mt-4 space-y-2.5">
            <input className="field !py-2.5" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" />
            <input className="field !py-2.5" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
            <input className="field !py-2.5" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
            <input className="field !py-2.5" value={driverAge} onChange={(e) => setDriverAge(e.target.value)} placeholder="Driver's age (optional)" inputMode="numeric" />
            <input className="field !py-2.5" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else? (optional)" />
          </div>
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden className="absolute left-[-9999px] h-0 w-0 opacity-0" />

          {days > 0 && (
            <div className="mt-5 space-y-2 border-t border-line pt-4 font-body text-sm">
              <div className="flex justify-between text-muted">
                <span>{formatPula(rate)} × {days} day{days === 1 ? '' : 's'}</span>
                <span className="text-espresso">{formatPula(total)}</span>
              </div>
              <div className="flex justify-between border-t border-line pt-2 font-display text-lg text-espresso">
                <span>Total</span><span>{formatPula(total)}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-md border border-blush-deep/40 bg-blush-soft/40 p-3">
              <p className="font-body text-sm text-espresso">{error}</p>
              <a href={waLink(biz.whatsapp, waText)} target="_blank" rel="noreferrer" className="btn-gold mt-2.5 !py-2 !text-[11px]"><MessageCircle size={13} /> Book on WhatsApp</a>
            </div>
          )}

          <button onClick={openConfirm} disabled={busy} className="btn-primary mt-5 w-full">
            {busy ? 'Sending…' : 'Request to hire'} <ArrowRight size={15} />
          </button>
          <p className="mt-3 flex items-center justify-center gap-1.5 font-body text-[11px] text-muted">
            <Check size={12} className="text-gold-deep" /> You won't be charged — we'll confirm availability by email.
          </p>
        </aside>
      </div>

      {confirming && (
        <Modal onClose={() => setConfirming(false)}>
            <h2 className="font-display text-2xl text-espresso">Confirm your hire</h2>
            <div className="rule-gold mt-3" />
            <div className="mt-5 space-y-2 font-body text-sm text-muted">
              <div className="flex justify-between"><span>Vehicle</span><span className="text-espresso">{car?.name}</span></div>
              <div className="flex justify-between"><span>Pick-up</span><span className="text-espresso">{niceDate(pickup)}</span></div>
              <div className="flex justify-between"><span>Return</span><span className="text-espresso">{niceDate(dropoff)}</span></div>
              <div className="flex justify-between border-t border-line pt-2 font-display text-lg text-espresso"><span>{days} day{days === 1 ? '' : 's'}</span><span>{formatPula(total)}</span></div>
            </div>
            <div className="mt-4 rounded-md bg-paper p-3">
              <p className="font-body text-xs leading-relaxed text-muted">
                Please bring a valid driver's licence and ID at collection. Minimum driver age is {HIRE_POLICY.minAge}. This is a request; we'll confirm availability by email and no payment is taken now.
              </p>
            </div>
            <div className="mt-6 flex gap-2.5">
              <button onClick={() => setConfirming(false)} className="btn-outline flex-1 justify-center">Back</button>
              <button onClick={submit} disabled={busy} className="btn-primary flex-1 justify-center">{busy ? 'Sending…' : 'Confirm & request'}</button>
            </div>
        </Modal>
      )}
    </div>
  );
}
